import agk from "./agk-docs.lite.json";

/**
 * Anchor Sprint AI Governance Kit (AGK) — the documents the website may show.
 *
 * Source: github.com/anchorsprint/ai-governance-kit, releases/<release>/.
 * agk-docs.json is generated by scripts/import-agk-kit.py from the kit's
 * access.json: only "public" and "free" documents are imported. "member"
 * documents must never be added here or copied into public/.
 *
 * Cite template versions (e.g. "AGK-POL-02 v1.1") wherever kit text is reused.
 */

export type AgkAccess = "public" | "free";

export interface AgkFile {
  name: string;
  path: string;
  format: string;
  bytes: number;
}

export interface AgkPagePreview {
  src: string;
  w: number;
  h: number;
}

/** What the client needs (agk-docs.lite.json). Preview HTML lives in kit-detail.ts. */
interface ImportedDoc {
  id: string;
  access: AgkAccess;
  files: AgkFile[];
  templateVersion: string;
  title?: string;
  kind?: string;
  words?: number;
  previewWords?: number;
  sheets?: Array<{ name: string; columns: string[] }>;
  /** Page images for the preview modal: first pages of free docs, all pages of public ones. */
  pages: AgkPagePreview[];
  totalPages: number;
}

export interface AgkDocMeta {
  id: string;
  /** URL segment under /ai-governance/templates/ (or a full href for docs with their own page). */
  slug: string;
  /** Overrides the template preview URL — POL-02 lives on the AUP template page. */
  href?: string;
  name: string;
  seoTitle: string;
  seoDescription: string;
  /** One-paragraph answer to "what is this document for?" */
  summary: string;
  usedBy: string;
  when: string;
  /** Language of the document itself. */
  lang: "en" | "ms";
  keywords: string[];
}

const META: AgkDocMeta[] = [
  {
    id: "AGK-001",
    slug: "ai-governance-lite-quick-start-checklist",
    name: "Lite Quick Start and Control Checklist",
    seoTitle: "AI Governance Checklist Template (Lite, 53 Controls)",
    seoDescription:
      "Preview the AI governance quick start checklist for Malaysian and Singapore companies: who owns AI, the 90-day plan and the 53 Lite controls. Free template.",
    summary:
      "The starting point for an organisation adopting AI governance for the first time. It names the owners, sets out the 90-day plan, and lists the 53 Lite controls as a checklist.",
    usedBy: "AI sponsor, AI Governance Lead",
    when: "Week 1, before anything else",
    lang: "en",
    keywords: ["AI governance checklist", "AI governance quick start", "AI governance SME Malaysia"],
  },
  {
    id: "AGK-000",
    slug: "ai-governance-framework",
    name: "AI Governance Framework and Document Architecture",
    seoTitle: "AI Governance Framework Template (ISO/IEC 42001-aligned)",
    seoDescription:
      "Preview an AI governance framework: document levels, tracks, tiers, roles and its mapping to ISO/IEC 42001, NIST AI RMF, PDPA and MAS. Free Word template.",
    summary:
      "The map of the whole kit. It explains the five document levels, the two tracks, the three adoption tiers, the shared definitions, and how every document maps to ISO/IEC 42001 and the NIST AI RMF.",
    usedBy: "AI Governance Lead, risk and compliance",
    when: "Standard and Regulated adopters, first read",
    lang: "en",
    keywords: ["AI governance framework template", "AI governance framework Malaysia", "ISO 42001 framework template"],
  },
  {
    id: "AGK-POL-02",
    slug: "ai-acceptable-use-policy",
    href: "/ai-governance/ai-acceptable-use-policy-template/",
    name: "AI Acceptable Use Policy",
    seoTitle: "AI Acceptable Use Policy Template",
    seoDescription: "",
    summary:
      "The staff-facing policy: approved tools only, data rules, human review, disclosure, connectors and agents, prohibited uses, and how to report problems. Every user acknowledges it before access.",
    usedBy: "HR, AI Governance Lead, all staff",
    when: "Week 2",
    lang: "en",
    keywords: ["AI acceptable use policy template"],
  },
  {
    id: "AGK-STD-A02",
    slug: "ai-data-handling-standard",
    name: "AI Data Handling Standard",
    seoTitle: "AI Data Handling Standard Template (Data × AI Tool Matrix)",
    seoDescription:
      "Preview a generative AI data handling standard: five data labels, four tool tiers and a matrix of what data may go into which AI tool, with PDPA controls.",
    summary:
      "Decides which data may go into which AI tool. Five data labels (Public to Secrets) against four tool tiers (consumer to agentic), plus the personal data (PDPA) controls that sit on top.",
    usedBy: "Data Protection Officer, IT Security",
    when: "Week 2, with the acceptable use policy",
    lang: "en",
    keywords: ["AI data classification policy", "generative AI data handling", "PDPA AI data"],
  },
  {
    id: "AGK-PRC-07",
    slug: "shadow-ai-discovery-procedure",
    name: "AI Discovery and Shadow AI Procedure",
    seoTitle: "Shadow AI Discovery Procedure Template",
    seoDescription:
      "Preview a procedure to find the AI tools staff already use: survey, expense and network discovery, a 30-day amnesty and the AI system register. Free template.",
    summary:
      "How to find the AI already in use before you write rules for it: staff survey, expense and SaaS data, network discovery, a no-blame amnesty, and recording what you find in the AI system register.",
    usedBy: "AI Governance Lead, IT",
    when: "Weeks 1–2",
    lang: "en",
    keywords: ["shadow AI", "shadow AI policy", "AI discovery procedure"],
  },
  {
    id: "AGK-GDL-C02",
    slug: "ai-adoption-roadmap-guide",
    name: "AI Adoption Roadmap and Use-Case Prioritisation Guide",
    seoTitle: "AI Adoption Roadmap and Use-Case Prioritisation Guide",
    seoDescription:
      "Preview a guide to picking your first AI use cases: a two-hour workshop, value, feasibility and risk scoring, and a roadmap governance can keep up with.",
    summary:
      "Keeps governance from slowing adoption. A two-hour workshop format, a scoring method for value, feasibility and risk, and how to turn the top ideas into two or three pilots with measurable benefits.",
    usedBy: "AI sponsor, business leads",
    when: "Weeks 4–6",
    lang: "en",
    keywords: ["AI adoption roadmap", "AI use case prioritisation", "AI roadmap Malaysia"],
  },
  {
    id: "AGK-TPL-01",
    slug: "ai-system-register-template",
    name: "AI System Register",
    seoTitle: "AI System Register Template (AI Inventory, Excel)",
    seoDescription:
      "Preview an AI inventory template: every AI tool and system with owner, vendor, data label, tool tier, autonomy level and risk tier. Free Excel register.",
    summary:
      "The inventory regulators and auditors ask for first: every AI tool and system, its owner, vendor, processing location, highest data label, tool tier, autonomy level and risk tier.",
    usedBy: "AI Governance Lead",
    when: "From week 1, kept current",
    lang: "en",
    keywords: ["AI inventory template", "AI system register", "AI register Excel"],
  },
  {
    id: "AGK-TPL-03",
    slug: "ai-use-case-intake-form",
    name: "AI Use Case Intake Form",
    seoTitle: "AI Use Case Intake Form Template",
    seoDescription:
      "Preview a one-form intake for new AI use cases: purpose, data, users, autonomy and the triage questions that route it to the right approver. Free Word template.",
    summary:
      "One form for anyone who wants to start using AI for something new. The answers decide the risk tier and who approves it, so low-risk ideas move fast and high-risk ones get a proper look.",
    usedBy: "Anyone proposing an AI use case",
    when: "Weeks 4–6, then ongoing",
    lang: "en",
    keywords: ["AI use case intake form", "AI request form", "AI project approval form"],
  },
  {
    id: "AGK-TPL-06",
    slug: "ai-incident-log-template",
    name: "AI Incident, Nonconformity and CAPA Log",
    seoTitle: "AI Incident Log Template (Incident, Nonconformity & CAPA)",
    seoDescription:
      "Preview an AI incident register: log, incident report, nonconformity and corrective action (CAPA) sheets with a priority matrix. Free Excel template.",
    summary:
      "Where AI incidents and near misses are recorded and closed out: an incident log, a report form, a nonconformity and corrective-action (CAPA) register, and a priority matrix.",
    usedBy: "AI Governance Lead, IT Security, DPO",
    when: "Weeks 4–6, before the first incident",
    lang: "en",
    keywords: ["AI incident log template", "AI incident register", "CAPA log template"],
  },
  {
    id: "AGK-TPL-07",
    slug: "ai-acceptable-use-acknowledgement-form",
    name: "AI Acceptable Use Acknowledgement",
    seoTitle: "AI Acceptable Use Acknowledgement Form Template",
    seoDescription:
      "Preview the staff acknowledgement form for an AI acceptable use policy: what each user signs before getting AI tool access. Free Word template, also in BM.",
    summary:
      "The one-page form every user signs before getting access to approved AI tools. It turns the acceptable use policy into a short list of personal commitments.",
    usedBy: "HR, every AI user",
    when: "Week 2, before access",
    lang: "en",
    keywords: ["AI policy acknowledgement form", "AI acceptable use agreement employees"],
  },
  {
    id: "AGK-TPL-07-BM",
    slug: "borang-pengakuan-penggunaan-ai",
    name: "Pengakuan Penggunaan AI (Bahasa Melayu)",
    seoTitle: "Borang Pengakuan Penggunaan AI (Templat BM)",
    seoDescription:
      "Pratonton borang pengakuan penggunaan AI dalam Bahasa Melayu untuk kakitangan sebelum akses kepada alat AI. Templat Word percuma.",
    summary:
      "The Bahasa Melayu version of the acknowledgement form, for staff who prefer to read and sign in BM.",
    usedBy: "HR, every AI user",
    when: "Week 2, before access",
    lang: "ms",
    keywords: ["borang pengakuan penggunaan AI", "dasar penggunaan AI syarikat"],
  },
  {
    id: "AGK-TPL-12",
    slug: "ai-use-case-prioritisation-tracker",
    name: "AI Use-Case Prioritisation and Benefits Tracker",
    seoTitle: "AI Use Case Prioritisation and Benefits Tracker (Excel)",
    seoDescription:
      "Preview an Excel tracker to score AI use cases on value, feasibility and risk, then track the benefits each pilot actually delivers. Free template.",
    summary:
      "Scores AI ideas on value, feasibility and risk, then tracks what each pilot actually delivers, so the AI committee can decide what to scale with numbers rather than opinions.",
    usedBy: "AI sponsor, AI Governance Lead",
    when: "Weeks 4–12",
    lang: "en",
    keywords: ["AI use case prioritisation template", "AI benefits tracking", "AI ROI tracker"],
  },
  {
    id: "AGK-GDL-A01",
    slug: "safe-ai-use-guide-for-staff",
    name: "Safe AI Use Guide for Staff",
    seoTitle: "Safe AI Use Guide for Staff (Free, Full Text)",
    seoDescription:
      "A plain-language guide for staff using ChatGPT, Claude, Copilot or Gemini at work: what data is safe, how to check output and what never to do. Free, full text.",
    summary:
      "The plain-language companion to the acceptable use policy, written for every employee. Read it in full here, free, no sign-up.",
    usedBy: "All staff",
    when: "Week 2, with training",
    lang: "en",
    keywords: ["safe AI use guide", "AI guidelines for employees", "ChatGPT guidelines for staff"],
  },
  {
    id: "AGK-GDL-A01-BM",
    slug: "panduan-penggunaan-ai-selamat",
    name: "Panduan Penggunaan AI Selamat (Bahasa Melayu)",
    seoTitle: "Panduan Penggunaan AI Selamat untuk Kakitangan",
    seoDescription:
      "Panduan ringkas untuk staf yang guna ChatGPT, Claude, Copilot atau Gemini di tempat kerja: data yang selamat, semakan output dan larangan. Teks penuh percuma.",
    summary:
      "The Bahasa Melayu version of the Safe AI Use Guide. Full text, free, no sign-up.",
    usedBy: "All staff",
    when: "Week 2, with training",
    lang: "ms",
    keywords: ["panduan penggunaan AI", "garis panduan AI kakitangan"],
  },
];

export type AgkDoc = AgkDocMeta & ImportedDoc;

const imported = agk.docs as unknown as Record<string, ImportedDoc>;

export const AGK_RELEASE = agk.release;
export const AGK_PACK = agk.pack as AgkFile;
export const AGK_PRE_READ = (imported["PRE-READ"] as ImportedDoc).files[0];
/** The kit pre-read (public PDF) as a previewable item for the modal. */
export const AGK_PRE_READ_DOC = {
  id: "PRE-READ",
  name: "AI Governance Kit pre-read",
  access: "public" as AgkAccess,
  templateVersion: (imported["PRE-READ"] as ImportedDoc).templateVersion,
  files: (imported["PRE-READ"] as ImportedDoc).files,
  pages: (imported["PRE-READ"] as ImportedDoc).pages,
  totalPages: (imported["PRE-READ"] as ImportedDoc).totalPages,
};

export const agkDocs: AgkDoc[] = META.map((meta) => {
  const doc = imported[meta.id];
  if (!doc) throw new Error(`AGK document ${meta.id} missing from agk-docs.json`);
  return { ...doc, ...meta };
});

/** Documents in the Lite pack download (the "free" tier of access.json). */
export const agkFreeDocs = agkDocs.filter((d) => d.access === "free");
/** Documents anyone can read and download without signing up. */
export const agkPublicDocs = agkDocs.filter((d) => d.access === "public");

/** Docs with their own preview page under /ai-governance/templates/. */
export const agkPreviewDocs = agkDocs.filter((d) => !d.href);

export function getAgkDoc(idOrSlug: string): AgkDoc | undefined {
  return agkDocs.find((d) => d.id === idOrSlug || d.slug === idOrSlug);
}

export function agkDocHref(doc: AgkDocMeta): string {
  return doc.href ?? `/ai-governance/templates/${doc.slug}/`;
}

/** "AGK-POL-02 v1.1" — the citation used wherever kit content is reused. */
export function agkCitation(doc: Pick<ImportedDoc, "id" | "templateVersion">): string {
  return `${doc.id} v${doc.templateVersion}`;
}

export function formatLabel(format: string): string {
  return format === "xlsx" ? "Excel" : format === "docx" ? "Word" : format.toUpperCase();
}

export function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Stages of AI adoption asked on the download form. */
export const ADOPTION_STAGES = [
  "Discovery",
  "Tooling onboarding",
  "Governance",
  "Training",
  "Value realisation",
  "Scale agentic",
] as const;

export const COMPANY_SIZES = ["1–49", "50–249", "250–999", "1,000+"] as const;
